import { EyeOutlined } from '@ant-design/icons';
import {
  Button,
  Descriptions,
  Drawer,
  Empty,
  message,
  Popover,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import React, { useState } from 'react';

import { fetchAPI } from '../../../utils/api';
import { OrderV2 } from './types';

type AmazonDeliveryStatusOrder = {
  orderId?: string;
  poNumber?: string;
  orderStatus?: string;
  orderDate?: string;
  buyerName?: string;
  buyerEmail?: string;
  totalAmount?: number;
  currency?: string;
  productName?: string;
  shippingStatus?: string;
  trackingNumber?: string;
  carrierName?: string;
  shipDate?: string;
  estimatedDeliveryDate?: string;
  deliveryStatus?: string;
  shippingAddress?: string;
};

type AmazonDeliveryStatusSummary = {
  totalOrders?: number;
  totalAmount?: number;
  currency?: string;
  allShipped?: boolean;
  allDelivered?: boolean;
  pendingCount?: number;
  shippedCount?: number;
  deliveredCount?: number;
};

type AmazonDeliveryStatusResponse = {
  success?: boolean;
  poNumber?: string;
  orders?: AmazonDeliveryStatusOrder[];
  summary?: AmazonDeliveryStatusSummary;
};

type AmazonDeliveryStatusAPIResponse =
  | AmazonDeliveryStatusResponse
  | {
      status?: string;
      result?: AmazonDeliveryStatusResponse;
    };

const renderText = (value?: string | number | boolean | null) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return String(value);
};

const normalizeAmazonDeliveryStatusResponse = (
  payload: AmazonDeliveryStatusAPIResponse
): AmazonDeliveryStatusResponse => {
  if (payload && typeof payload === 'object' && 'result' in payload) {
    return payload.result || {};
  }

  return (payload || {}) as AmazonDeliveryStatusResponse;
};

const AmazonOrderDeliveryStatus = (props: { record: OrderV2 }) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<AmazonDeliveryStatusResponse | null>(
    null
  );

  const handleAction = async (isSendEmail: boolean) => {
    setMenuVisible(false);
    setDrawerVisible(true);
    setLoading(true);

    try {
      const resp = (await fetchAPI('order/amazon/delivery-status', {
        method: 'POST',
        payload: {
          order_id: props.record.id,
          is_send_email: isSendEmail,
        },
      })) as AmazonDeliveryStatusAPIResponse;

      setResponse(normalizeAmazonDeliveryStatusResponse(resp));
      if (isSendEmail) {
        message.success('Delivery status loaded. Email request submitted.');
      }
    } catch (error) {
      setDrawerVisible(false);
      setResponse(null);
      message.error('Failed get amazon delivery status');
    } finally {
      setLoading(false);
    }
  };

  const orders = response?.orders || [];

  const columns = [
    {
      title: 'PO Number',
      dataIndex: 'poNumber',
      key: 'poNumber',
      width: 140,
      render: value => renderText(value),
    },
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 180,
      render: value => renderText(value),
    },
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
      width: 260,
      render: value => renderText(value),
    },
    {
      title: 'Order Status',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      width: 120,
      render: value => <Tag>{renderText(value)}</Tag>,
    },
    {
      title: 'Shipping Status',
      dataIndex: 'shippingStatus',
      key: 'shippingStatus',
      width: 140,
      render: value => <Tag color="blue">{renderText(value)}</Tag>,
    },
    {
      title: 'Delivery Status',
      dataIndex: 'deliveryStatus',
      key: 'deliveryStatus',
      width: 140,
      render: value => <Tag color="green">{renderText(value)}</Tag>,
    },
    {
      title: 'Buyer',
      key: 'buyer',
      width: 180,
      render: (_, record: AmazonDeliveryStatusOrder) => (
        <div>
          <div>{renderText(record.buyerName)}</div>
          <div style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
            {renderText(record.buyerEmail)}
          </div>
        </div>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 120,
      render: (_, record: AmazonDeliveryStatusOrder) =>
        `${renderText(record.currency)} ${renderText(record.totalAmount)}`,
    },
    {
      title: 'Tracking',
      key: 'tracking',
      width: 180,
      render: (_, record: AmazonDeliveryStatusOrder) => (
        <div>
          <div>{renderText(record.trackingNumber)}</div>
          <div style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
            {renderText(record.carrierName)}
          </div>
        </div>
      ),
    },
    {
      title: 'Dates',
      key: 'dates',
      width: 220,
      render: (_, record: AmazonDeliveryStatusOrder) => (
        <div>
          <div>Order: {renderText(record.orderDate)}</div>
          <div>Ship: {renderText(record.shipDate)}</div>
          <div>ETA: {renderText(record.estimatedDeliveryDate)}</div>
        </div>
      ),
    },
    {
      title: 'Shipping Address',
      dataIndex: 'shippingAddress',
      key: 'shippingAddress',
      width: 280,
      render: value => renderText(value),
    },
  ];

  return (
    <>
      <Popover
        title={null}
        trigger="click"
        placement="rightTop"
        visible={menuVisible}
        onVisibleChange={visible => setMenuVisible(visible)}
        content={
          <Space direction="vertical" size={0} style={{ minWidth: 220 }}>
            <Button
              type="link"
              style={{ padding: 0, textAlign: 'left' }}
              onClick={() => handleAction(false)}
            >
              View detail only
            </Button>
            <Button
              type="link"
              style={{ padding: 0, textAlign: 'left' }}
              onClick={() => handleAction(true)}
            >
              View detail and send email
            </Button>
          </Space>
        }
      >
        <Tooltip title="Check Amazon delivery status" placement="topRight">
          <Button
            icon={<EyeOutlined />}
            size="small"
            style={{ fontSize: 12, marginTop: 8 }}
          >
            Amazon Order
          </Button>
        </Tooltip>
      </Popover>

      <Drawer
        title={
          <Space>
            <span>Amazon Delivery Status</span>
            <Tag color="blue">Order {props.record.id}</Tag>
          </Space>
        }
        visible={drawerVisible}
        width={960}
        onClose={() => {
          setDrawerVisible(false);
          setLoading(false);
        }}
      >
        <Spin spinning={loading}>
          {response ? (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="PO Number">
                  {renderText(response.poNumber)}
                </Descriptions.Item>
                <Descriptions.Item label="Success">
                  {renderText(response.success)}
                </Descriptions.Item>
                <Descriptions.Item label="Total Orders">
                  {renderText(response.summary?.totalOrders)}
                </Descriptions.Item>
                <Descriptions.Item label="Total Amount">
                  {`${renderText(response.summary?.currency)} ${renderText(
                    response.summary?.totalAmount
                  )}`}
                </Descriptions.Item>
                <Descriptions.Item label="Pending Count">
                  {renderText(response.summary?.pendingCount)}
                </Descriptions.Item>
                <Descriptions.Item label="Shipped Count">
                  {renderText(response.summary?.shippedCount)}
                </Descriptions.Item>
                <Descriptions.Item label="Delivered Count">
                  {renderText(response.summary?.deliveredCount)}
                </Descriptions.Item>
                <Descriptions.Item label="All Delivered">
                  {renderText(response.summary?.allDelivered)}
                </Descriptions.Item>
                <Descriptions.Item label="All Shipped">
                  {renderText(response.summary?.allShipped)}
                </Descriptions.Item>
              </Descriptions>

              <Table
                size="small"
                bordered
                rowKey={record =>
                  record.orderId || record.poNumber || Math.random()
                }
                columns={columns}
                dataSource={orders}
                pagination={false}
                locale={{
                  emptyText: <Empty description="No Amazon delivery details" />,
                }}
                scroll={{ x: 1800 }}
              />
            </Space>
          ) : (
            <Empty description="No Amazon delivery details" />
          )}
        </Spin>
      </Drawer>
    </>
  );
};

export default AmazonOrderDeliveryStatus;
